import { z } from 'zod';
import type { GameSettings } from './game-ui-tokens';

/**
 * Messages between a game (in a frame on the game domain, using @mogzo/sdk) and the
 * mogzo.com page around it (PRD 5.3). Every message carries `mogzo: 1`; the page answers
 * only its own frame, at the game's exact origin, and checks every message against these
 * schemas, because the game is code MOGZO didn't write.
 */
export const BRIDGE_VERSION = 1;

/** Methods a game can call in this version; anything else is answered with an error. */
export const BRIDGE_METHODS = [
  'player.me',
  'post.context',
  'save.get',
  'save.set',
  'save.delete',
  'save.list',
  'data.get',
  'data.set',
  'data.delete',
  'data.query',
  'config.get',
  'events.active',
  'leaderboards.submit',
  'leaderboards.top',
  'leaderboards.aroundMe',
  'achievements.unlock',
  'achievements.progress',
  'achievements.list',
  'launch',
  'ui.showLeaderboard',
  'ui.showAchievements',
  'ui.showPauseMenu',
  'ui.promptSignIn',
  'multiplayer.find',
  'multiplayer.create',
  'multiplayer.join',
  'multiplayer.end',
  'multiplayer.leave',
  'matches.mine',
  'matches.open',
  'matches.move',
  'matches.wait',
  'bases.save',
  'bases.mine',
  'bases.findOpponent',
  'bases.reportAttack',
  'bases.inbox',
  'rooms.ticket',
  'rooms.connection',
  'chat.open',
  'chat.close',
  'chat.say',
] as const;
export type BridgeMethod = (typeof BRIDGE_METHODS)[number];

/**
 * Which page is talking: the game's splash (in a post, before anyone plays) or the game
 * itself, launched from it.
 */
export const GAME_VIEWS = ['splash', 'game'] as const;
export type GameView = (typeof GAME_VIEWS)[number];

/** Things the page tells the game without being asked. */
export const GAME_EVENTS = ['pause', 'resume', 'settings', 'phrase'] as const;
export type GameEvent = (typeof GAME_EVENTS)[number];

/**
 * Chat in a match (M38b): `chat.open` opens MOGZO's chat in the mode's look, or the one asked
 * for; `chat.say` sends one of the match's quick phrases. The game never reads typed messages:
 * it only hears phrases (`phrase` events), to show over the player who sent one.
 */
export const chatOpenParamsSchema = z.object({ look: z.enum(['panel', 'compact']).optional() });
export const chatSayParamsSchema = z.object({ phrase: z.string().trim().min(1).max(20) });

/** A quick phrase someone in the match sent (this player's too, with `mine`). */
export interface ChatPhrase {
  player: BridgePlayer;
  phrase: string;
  mine: boolean;
}

/** `ui.showLeaderboard`'s arguments: which board, and which view and period to open on. */
export const showLeaderboardParamsSchema = z.object({
  id: z.string().max(64).optional(),
  view: z.enum(['top', 'around']).default('top'),
  period: z.enum(['current', 'previous']).default('current'),
});
export type ShowLeaderboardParams = z.infer<typeof showLeaderboardParamsSchema>;

/**
 * What a splash passes to the game it launches, such as `{ mode: 'nets' }`: up to 10 short
 * text values. The game reads them with `mogzo.launchParams()`.
 */
export const launchParamsSchema = z
  .record(
    z.string().regex(/^[A-Za-z0-9_-]{1,32}$/, 'Name launch params with up to 32 letters or digits'),
    z.string().max(200, 'A launch param can be up to 200 characters'),
  )
  .refine((params) => Object.keys(params).length <= 10, 'Up to 10 launch params');
export type LaunchParams = Record<string, string>;

/** The `launch` call's arguments. */
export const launchRequestSchema = z.object({ params: launchParamsSchema.default({}) });

/** What a game learns about the player: never an email, account id or other games. */
export interface BridgePlayer {
  /** Stable for this player in this game only. */
  id: string;
  name: string;
}

const messageId = z.string().min(1).max(64);

/** Game → page. */
export const gameMessageSchema = z.discriminatedUnion('type', [
  z.object({
    mogzo: z.literal(BRIDGE_VERSION),
    type: z.literal('hello'),
    id: messageId,
    sdkVersion: z.string().max(32),
  }),
  z.object({
    mogzo: z.literal(BRIDGE_VERSION),
    type: z.literal('request'),
    id: messageId,
    method: z.string().max(64),
    params: z.unknown().optional(),
  }),
]);
export type GameMessage = z.infer<typeof gameMessageSchema>;

/** Page → game. */
export type PageMessage =
  | {
      mogzo: 1;
      type: 'welcome';
      id: string;
      /** Null for guests. */
      player: BridgePlayer | null;
      /** The game post the game was opened from (PRD 4.4); always null until posts exist. */
      post: null;
      /** Missing from pages older than M18c, which only ran games. */
      view?: GameView;
      /** What the splash launched the game with; empty for a splash. */
      launch?: LaunchParams;
      /** The player's choices in the pause menu (M19). */
      settings?: GameSettings;
      /** An invite code the player opened the game with, from a match link (M31). */
      invite?: string | null;
      /** A match the player opened the game for, from a notification or email (M33). */
      openMatch?: string | null;
    }
  | { mogzo: 1; type: 'response'; id: string; result: unknown }
  | { mogzo: 1; type: 'error'; id: string; code: string; message: string }
  | { mogzo: 1; type: 'event'; name: GameEvent; settings?: GameSettings; phrase?: ChatPhrase };
